import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../Loader";
import "../addons.css";
import AuthContext from "../../../contexts/AuthProvider";
import { useContext } from "react";
import { API_URL } from "../../../utils/config";
const baseUrl = API_URL;

const EditDish = ({ onClose, catering_id, venue_id }) => {
  const { t } = useTranslation("dish");
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    dish_name: "",
    venue_id: "",
    cuisine_type: "",
    cost: "",
    ingredients: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
   const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
        const { isAuthenticated } = useContext(AuthContext);

  const fetchVenues = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return [];
      }
      const res = await fetch(`${baseUrl}/api/v1/attnd-venue/assignedVenues`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),

        },
      });
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("editDish.errors.unauthorized"));
      }
      if (!res.ok) {
        throw new Error(t("editDish.errors.fetchFailed"));
      }
      const json = await res.json();
      return json.data;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
         
        navigate("/login");
      }
      return [];
    }
  };

  const fetchCatering = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/");
        return null;
      }
      const res = await fetch(
        `${baseUrl}/api/v1/attnd-venue/caterings/${catering_id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("editDish.errors.unauthorized"));
      }
      if (!res.ok) {
        throw new Error(t("editDish.errors.fetchFailed"));
      }
      const json = await res.json();
      return json.data;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
         
        navigate("/login");
      }
      return null;
    }
  };

  const initializeFormFields = (catering) => {
    setFormData({
      dish_name: catering.dish_name,
      venue_id: catering.venue_id,
      cuisine_type: catering.cuisine_type,
      cost: catering.cost,
      ingredients: Array.isArray(catering.ingredients)
        ? catering.ingredients.join(", ")
        : catering.ingredients,
    });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [venuesData, cateringData] = await Promise.all([
        fetchVenues(),
        fetchCatering()
      ]);
      setVenues(venuesData);
      if (cateringData) initializeFormFields(cateringData);
      setLoading(false);
    })();
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "dish_name":
        return value
          ? ""
          : t("editDish.errors.dishNameRequired");
      case "venue_id":
        return value
          ? ""
          : t("editDish.errors.venueRequired");
      case "cuisine_type":
        return value
          ? ""
          : t("editDish.errors.cuisineTypeRequired");
      case "cost":
        if (!value) return t("editDish.errors.costRequired");
        if (parseFloat(value) <= 0) return t("editDish.errors.costPositive");
        return "";
      case "ingredients":
        return value
          ? ""
          : t("editDish.errors.ingredientsRequired");
      default:
        return "";
    }
  };

  const isFormValid = () =>
    ["dish_name", "venue_id", "cuisine_type", "cost", "ingredients"].every(
      (field) => validateField(field, formData[field]) === ""
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cost" && parseFloat(value) < 0) {
      setErrorMessages((prev) => ({
        ...prev,
        [name]: t("editDish.errors.costPositive"),
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMessages((prev) => ({
      ...prev,
      [name]: validateField(name, value) || undefined,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "-") e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const currentErrors = Object.keys(formData).reduce((errs, field) => {
      const err = validateField(field, formData[field]);
      if (err) errs[field] = err;
      return errs;
    }, {});
    if (Object.keys(currentErrors).length) {
      setErrorMessages(currentErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      if (!isAuthenticated) {
        navigate("/");
        return;
      }
      const ingredientsArray = formData.ingredients
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");
      const dish = {
        dish_name: formData.dish_name,
        venue_id: parseInt(formData.venue_id),
        cost: parseFloat(formData.cost),
        cuisine_type: formData.cuisine_type,
        ingredients: ingredientsArray,
      };

      const res = await fetch(`${baseUrl}/api/v1/attnd-venue/caterings/${catering_id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
        body: JSON.stringify(dish),
      });
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("editDish.errors.unauthorized"));
      }
      if (!res.ok) {
        const errJson = await res.json();
        if (errJson.errors) {
          const backendErrors = errJson.errors.reduce((acc, err) => {
            const field = Object.keys(err)[0];
            acc[field] = err[field];
            return acc;
          }, {});
          setErrorMessages(backendErrors);
        } else {
          throw new Error(errJson.error || t("editDish.errors.updateFailed"));
        }
        return;
      }
      await res.json();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMessages({ message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="addons-loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="invite-attendee-modal">
      <div className="form-invite-attendee">
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("editDish.labels.dishName")}:
              </label>
              <input
                type="text"
                name="dish_name"
                className="form-input"
                placeholder={t("editDish.placeholders.dishName")}
                value={formData.dish_name}
                onChange={handleChange}
                required
              />
              {errorMessages.dish_name && (
                <span className="error-messages">
                  {errorMessages.dish_name}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("editDish.labels.venue")}:
              </label>
              <div className="form-select-container">
                <select
                  name="venue_id"
                  className="form-select"
                  value={formData.venue_id}
                  onChange={handleChange}
                  disabled={loading}
                  required
                >
                  <option value="">
                    {t("editDish.placeholders.selectVenue")}
                  </option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              {errorMessages.venue_id && (
                <span className="error-messages">
                  {errorMessages.venue_id}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("editDish.labels.cuisineType")}:
              </label>
              <div className="form-select-container">
                <select
                  name="cuisine_type"
                  className="form-select"
                  value={formData.cuisine_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    {t("editDish.placeholders.selectCuisineType")}
                  </option>
                  {Object.entries(
                    t("editDish.options.cuisineType", { returnObjects: true })
                  ).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {errorMessages.cuisine_type && (
                <span className="error-messages">
                  {errorMessages.cuisine_type}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("editDish.labels.ingredients")}:
              </label>
              <input
                type="text"
                name="ingredients"
                className="form-input"
                placeholder={t("editDish.placeholders.ingredients")}
                value={formData.ingredients}
                onChange={handleChange}
                required
              />
              {errorMessages.ingredients && (
                <span className="error-messages">
                  {errorMessages.ingredients}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("editDish.labels.cost")}:
              </label>
              <input
                type="number"
                name="cost"
                className="form-input"
                placeholder={t("editDish.placeholders.cost")}
                value={formData.cost}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
              />
              {errorMessages.cost && (
                <span className="error-messages">{errorMessages.cost}</span>
              )}
            </div>
          </div>

          {errorMessages.message && (
            <p className="error-messages" style={{ marginBottom: "15px" }}>
              {errorMessages.message}
            </p>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("editDish.buttons.cancel")}
            </button>
            <button
              type="submit"
              className="invite-button"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting
                ? t("editDish.buttons.submitting")
                : t("editDish.buttons.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDish;
